/** Display helpers shared by every screen. Mirrors ui/campaign-portal/src/lib.ts.
 *
 *  Timestamps are honest UTC at rest (§3.23); IST is a DISPLAY concern. Everything
 *  here pins Asia/Kolkata explicitly so the portal reads the same on an operator's
 *  laptop, on a machine left in another timezone, and in a screenshot.
 */
import { CallWindow } from './types';

export const IST = 'Asia/Kolkata';

const dayTimeFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: IST, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
});
const timeFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: IST, hour: 'numeric', minute: '2-digit', hour12: true,
});
const dayFmt = new Intl.DateTimeFormat('en-IN', { timeZone: IST, month: 'short', day: 'numeric' });
const longDayFmt = new Intl.DateTimeFormat('en-IN', {
  timeZone: IST, weekday: 'long', day: 'numeric', month: 'long',
});
const dayKeyFmt = new Intl.DateTimeFormat('en-CA', { timeZone: IST, dateStyle: 'short' });

export const fmtDayTime = (iso: string) => dayTimeFmt.format(new Date(iso));
export const fmtTime = (iso: string) => timeFmt.format(new Date(iso));
export const fmtDay = (iso: string) => dayFmt.format(new Date(iso));
export const fmtLongDay = (iso: string) => longDayFmt.format(new Date(iso));

/** IST calendar day as YYYY-MM-DD — the key everything groups by. */
export const istDay = (iso: string): string => dayKeyFmt.format(new Date(iso));
export const istToday = (): string => dayKeyFmt.format(new Date());
const hourFmt = new Intl.DateTimeFormat('en-GB', { timeZone: IST, hour: 'numeric', hour12: false });
/** The current IST hour, 0–23. */
export const istHour = (): number => Number(hourFmt.format(new Date())) % 24;

/** Shift a YYYY-MM-DD day key by whole days. */
export const shiftDay = (day: string, delta: number): string => {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
};

/** "in 2h 10m" / "1h 55m late" — the queue's relative clock. */
export const fmtGap = (iso: string, now: Date = new Date()): string => {
  const mins = Math.round((new Date(iso).getTime() - now.getTime()) / 60000);
  const late = mins < 0;
  const a = Math.abs(mins);
  const body =
    a < 1 ? 'now'
      : a < 60 ? `${a}m`
        : a < 1440 ? `${Math.floor(a / 60)}h ${a % 60}m`
          : `${Math.floor(a / 1440)}d`;
  if (body === 'now') return 'now';
  return late ? `${body} late` : `in ${body}`;
};

/** "3 hours ago" — coarse, past-only. */
export const fmtAgo = (iso: string, now: Date = new Date()): string => {
  const mins = Math.round((now.getTime() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
};

export const isPast = (iso: string, now: Date = new Date()) => new Date(iso).getTime() < now.getTime();

const istClockFmt = new Intl.DateTimeFormat('en-GB', {
  timeZone: IST, hour: '2-digit', minute: '2-digit', hour12: false,
});

/** The calling window an instant falls inside — strictly `[start, end)`, so the
 *  gaps between windows belong to none of them. Windows come from core
 *  (`GET /api/v1/scheduling/windows`), which reads the policy file; the ranges
 *  are never written down here. */
export const callWindowOf = (iso: string, windows: CallWindow[]): CallWindow | undefined => {
  const clock = istClockFmt.format(new Date(iso));  // "18:30", zero-padded → string compare is clock order
  return windows.find((w) => clock >= w.start && clock < w.end);
};

/**
 * Past its time. A task the scheduler placed sits at the START of its calling
 * window, so a call placed at 18:00 has until 20:00 — its window's close is the
 * deadline, not its due instant. A task in no window (a Sell.do follow-up, a
 * welcome call — a time we didn't choose) is late the moment it passes, as
 * before, and so is everything when no windows have loaded.
 */
export const isLate = (iso: string, windows: CallWindow[], now: Date = new Date()): boolean => {
  const window = callWindowOf(iso, windows);
  const deadline = window ? `${istDay(iso)}T${window.end}:00+05:30` : iso;   // IST has no DST
  return isPast(deadline, now);
};

/** "12%" of a denominator; empty when there is nothing to divide by. */
export const pct = (n: number, of: number): string => (of > 0 ? `${Math.round((n / of) * 100)}%` : '');

/** Chatbot-DB timestamps are IST-naive — pin them to +05:30 before comparing with UTC. */
export const parseChatTime = (dateTime: string): Date => {
  const hasZone = /Z$|[+-]\d{2}:?\d{2}$/.test(dateTime);
  return new Date(hasZone ? dateTime : `${dateTime}+05:30`);
};

/** Core's naive-UTC columns (leads.created_at) ship zone-less — pin them to Z,
 *  or the browser reads them as local time (CLAUDE.md §3.23). */
export const parseCoreTime = (dateTime: string): Date => {
  const hasZone = /Z$|[+-]\d{2}:?\d{2}$/.test(dateTime);
  return new Date(hasZone ? dateTime : `${dateTime}Z`);
};

/** snake_case → readable. Used on stages, task types, tag values. */
export const label = (s: string) => s.replace(/_/g, ' ');

/** The one ladder (core lead_stages.STAGES), in ladder order. */
export const STAGES = [
  'new', 'attempting', 'contacted', 'engaged', 'followup', 'visit_intent',
  'visit_scheduled', 'visited', 'revisit_scheduled', 'revisited', 'negotiation', 'booked',
  'unreachable', 'gone_quiet', 'deferred', 'lost', 'disqualified', 'unknown',
] as const;

export const STAGE_LABEL: Record<string, string> = {
  new: 'New', attempting: 'Trying', contacted: 'Reached', engaged: 'Engaged',
  followup: 'Follow-up', visit_intent: 'Wants visit', visit_scheduled: 'Visit fixed',
  visited: 'Visited', revisit_scheduled: 'Re-visit fixed', revisited: 'Re-visited',
  negotiation: 'Negotiation', booked: 'Booked', unreachable: 'Unreachable', gone_quiet: 'Gone quiet',
  deferred: 'Deferred', lost: 'Lost', disqualified: 'Disqualified', unknown: 'Unknown',
};

const EXITS = new Set(['lost', 'disqualified']);

/** One mapping from stage → chip class, used by every surface that shows a stage. */
export const stageChip = (stage: string | null): string => {
  if (!stage) return 'chip-mute';
  if (EXITS.has(stage)) return 'chip-brick';
  if (['visited', 'revisit_scheduled', 'revisited', 'negotiation', 'booked'].includes(stage)) return 'chip-forest';
  if (stage === 'visit_scheduled' || stage === 'visit_intent') return 'chip-gold';
  return 'chip-sky';
};
